import { useState } from "react";
import { apiClient } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquarePlus, Send, MessageSquare } from "lucide-react";
import { formatDateTime } from "@/lib/dataroom";

const STATUS = {
  open: { label: "Åpen", style: "bg-amber-100 text-amber-700" },
  answered: { label: "Besvart", style: "bg-emerald-100 text-emerald-700" },
  closed: { label: "Lukket", style: "bg-slate-100 text-slate-600" },
};

function QuestionCard({ deal, q, canManage }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState("");

  const replyMutation = useMutation({
    mutationFn: (body) => apiClient.post(`/deals/${deal.id}/questions/${q.id}/messages`, { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-qa", deal.id] });
      qc.invalidateQueries({ queryKey: ["deal-activity", deal.id] });
      setReply("");
    },
    onError: () => toast.error("Kunne ikke sende svar"),
  });

  const statusMutation = useMutation({
    mutationFn: (status) => apiClient.put(`/deals/${deal.id}/questions/${q.id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-qa", deal.id] }),
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 p-5" data-testid={`question-${q.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold text-brand-ink">{q.subject}</h4>
          <p className="text-xs text-slate-400 mt-0.5">Stilt av {q.asked_by_name || q.asked_by}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`border-0 ${STATUS[q.status]?.style}`}>{STATUS[q.status]?.label}</Badge>
          {canManage && q.status !== "closed" && (
            <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => statusMutation.mutate("closed")}>Lukk</Button>
          )}
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {(q.messages || []).map((m, i) => (
          <div key={i} className="flex gap-3">
            <span className="w-8 h-8 rounded-full bg-brand-ink text-brand-lime flex items-center justify-center text-[11px] font-bold shrink-0">
              {(m.author_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-400">{m.author_name} · {formatDateTime(m.created_at)}</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</p>
            </div>
          </div>
        ))}
      </div>
      {q.status !== "closed" && (
        <div className="mt-4 flex items-center gap-2">
          <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Skriv et svar..." data-testid={`reply-input-${q.id}`}
            onKeyDown={(e) => { if (e.key === "Enter" && reply.trim()) replyMutation.mutate(reply.trim()); }} />
          <Button size="icon" disabled={!reply.trim() || replyMutation.isPending} onClick={() => replyMutation.mutate(reply.trim())} data-testid={`send-reply-${q.id}`}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function QATab({ deal, canManage }) {
  const qc = useQueryClient();
  const dealId = deal.id;
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["deal-qa", dealId],
    queryFn: async () => (await apiClient.get(`/deals/${dealId}/questions`)).data,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => apiClient.post(`/deals/${dealId}/questions`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-qa", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      qc.invalidateQueries({ queryKey: ["deal-activity", dealId] });
      setSubject(""); setBody(""); setShowNew(false);
      toast.success("Spørsmål sendt");
    },
    onError: () => toast.error("Kunne ikke sende spørsmål"),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowNew((v) => !v)} data-testid="new-question-button">
          <MessageSquarePlus className="w-4 h-4 mr-2" /> Nytt spørsmål
        </Button>
      </div>

      {showNew && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-5 space-y-3">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Emne" data-testid="question-subject-input" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Skriv spørsmålet ditt..." rows={3} data-testid="question-body-input" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowNew(false)}>Avbryt</Button>
            <Button disabled={!subject.trim() || createMutation.isPending} onClick={() => createMutation.mutate({ subject: subject.trim(), body: body.trim() })} data-testid="submit-question">
              Send spørsmål
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-28 bg-white rounded-2xl animate-pulse" />)}</div>
      ) : questions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200/70">
          <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Ingen spørsmål ennå</p>
        </div>
      ) : (
        questions.map((q) => <QuestionCard key={q.id} deal={deal} q={q} canManage={canManage} />)
      )}
    </div>
  );
}
